import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import PrivateRoute from './utils/PrivateRoute';

// Components
import TopNavbar from './components/Layout/TopNavbar';
import Login from './components/Auth/Login';

// Pages
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import OffersManagementPage from './pages/OffersManagementPage';
import RequestsManagementPage from './pages/RequestsManagementPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import NotaryOfficesPage from './pages/NotaryOfficesPage';
import TutorialsPage from './pages/TutorialsPage';
import FAQsPage from './pages/FAQsPage';
import NotificationsPage from './pages/NotificationsPage';
import FeaturesPage from './pages/FeaturesPage';
import LocationsPage from './pages/LocationsPage';

import './App.css';

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t, i18n } = useTranslation();

  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-layout top-nav-layout">
      <TopNavbar />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} />
          <Route path="/offers" element={<PrivateRoute><OffersManagementPage /></PrivateRoute>} />
          <Route path="/requests" element={<PrivateRoute><RequestsManagementPage /></PrivateRoute>} />
          <Route path="/subscriptions" element={<PrivateRoute><SubscriptionsPage /></PrivateRoute>} />
          <Route path="/features" element={<PrivateRoute><FeaturesPage /></PrivateRoute>} />
          <Route path="/notary-offices" element={<PrivateRoute><NotaryOfficesPage /></PrivateRoute>} />
          <Route path="/tutorials" element={<PrivateRoute><TutorialsPage /></PrivateRoute>} />
          <Route path="/faqs" element={<PrivateRoute><FAQsPage /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
          <Route path="/locations" element={<PrivateRoute><LocationsPage /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
